import { useState, type MouseEvent } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, Loader2, Check, X } from 'lucide-react'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { TinyTalkLogoFull } from '@/components/app/Logo'
import { useAuth } from '@/db/queries/useAuth'
import { toast } from 'sonner'

// Extended register schema with password confirmation
const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email format'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'You must accept the terms and conditions',
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
})

type RegisterFormData = z.infer<typeof registerSchema>

const authInputClass =
  'border-transparent bg-slate-100 shadow-none placeholder:text-slate-400 cursor-text select-text selection:bg-blue-500 selection:text-white focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-primary/30'

const Register = () => {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const { registerMutation } = useAuth()

  const selectAllText = (event: MouseEvent<HTMLInputElement>) => {
    event.currentTarget.select()
  }

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange', // Clear errors as user types after first submit
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    },
  })

  const password = watch('password', '')

  // Password strength indicators
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
  }

  const onSubmit = async (data: RegisterFormData) => {
    try {
      // Real API mode
      const response = await registerMutation.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
      })

      if (response) {
        const tokenKey = import.meta.env.VITE_TOKEN_NAME || 'token'
        localStorage.removeItem(tokenKey)
        toast.success('Account created successfully! Please login to continue.')
        navigate({ to: '/auth/login' })
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Registration failed. Please try again.')
    }
  }

  return (
    <div
      className="flex min-h-[100dvh] flex-col items-center justify-start overflow-x-hidden overflow-y-auto bg-gradient-to-b from-teal-50 via-white to-blue-50 px-3 py-6 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[max(0.75rem,env(safe-area-inset-top))] sm:items-center sm:justify-center sm:p-4 sm:py-8"
      style={{ scrollbarGutter: 'stable' }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-4 flex justify-center sm:mb-8">
          <Link to="/" className="inline-block min-w-0 max-w-[min(100%,280px)] scale-[0.92] transition-transform sm:max-w-none sm:scale-100">
            <TinyTalkLogoFull />
          </Link>
        </div>

        <Card className="border-transparent shadow-lg sm:rounded-xl">
          <CardHeader className="space-y-1 px-4 pt-5 text-center sm:px-6 sm:pt-6">
            <CardTitle className="text-xl font-bold sm:text-2xl">Create an Account</CardTitle>
            <CardDescription className="text-sm sm:text-base">
              <span className="hidden sm:inline">Start assessing your child&apos;s speech development today</span>
              <span className="sm:hidden">Speech screening for your child</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-5 sm:px-6 sm:pb-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Name Fields — single column on mobile */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    placeholder="Enter first name"
                    autoComplete="given-name"
                    autoFocus
                    {...register('firstName')}
                    onClick={selectAllText}
                    className={`${authInputClass} ${errors.firstName ? 'border-red-500 focus-visible:ring-red-200' : ''}`}
                  />
                  {errors.firstName && (
                    <p className="text-xs text-red-500">{errors.firstName.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    placeholder="Enter last name"
                    autoComplete="family-name"
                    {...register('lastName')}
                    onClick={selectAllText}
                    className={`${authInputClass} ${errors.lastName ? 'border-red-500 focus-visible:ring-red-200' : ''}`}
                  />
                  {errors.lastName && (
                    <p className="text-xs text-red-500">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  autoComplete="email"
                  {...register('email')}
                  onClick={selectAllText}
                  className={`${authInputClass} ${errors.email ? 'border-red-500 focus-visible:ring-red-200' : ''}`}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter password"
                    autoComplete="new-password"
                    {...register('password')}
                    onClick={selectAllText}
                    className={`${authInputClass} pr-10 ${errors.password ? 'border-red-500 focus-visible:ring-red-200' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>

                {/* Password strength — compact row on mobile, list on sm+ */}
                {password && (
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 sm:flex-col sm:gap-x-0 sm:space-y-1">
                    <div className="flex min-w-0 items-center gap-1.5 text-[10px] sm:gap-2 sm:text-xs">
                      {passwordChecks.length ? (
                        <Check className="h-3 w-3 shrink-0 text-green-500" />
                      ) : (
                        <X className="h-3 w-3 shrink-0 text-red-500" />
                      )}
                      <span className={passwordChecks.length ? 'text-green-600' : 'text-gray-500'}>
                        <span className="sm:hidden">8+ chars</span>
                        <span className="hidden sm:inline">At least 8 characters</span>
                      </span>
                    </div>
                    <div className="flex min-w-0 items-center gap-1.5 text-[10px] sm:gap-2 sm:text-xs">
                      {passwordChecks.uppercase ? (
                        <Check className="h-3 w-3 shrink-0 text-green-500" />
                      ) : (
                        <X className="h-3 w-3 shrink-0 text-red-500" />
                      )}
                      <span className={passwordChecks.uppercase ? 'text-green-600' : 'text-gray-500'}>
                        <span className="sm:hidden">Uppercase</span>
                        <span className="hidden sm:inline">One uppercase letter</span>
                      </span>
                    </div>
                    <div className="flex min-w-0 items-center gap-1.5 text-[10px] sm:gap-2 sm:text-xs">
                      {passwordChecks.number ? (
                        <Check className="h-3 w-3 shrink-0 text-green-500" />
                      ) : (
                        <X className="h-3 w-3 shrink-0 text-red-500" />
                      )}
                      <span className={passwordChecks.number ? 'text-green-600' : 'text-gray-500'}>
                        <span className="sm:hidden">Number</span>
                        <span className="hidden sm:inline">One number</span>
                      </span>
                    </div>
                  </div>
                )}

                {errors.password && (
                  <p className="text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm password"
                    autoComplete="new-password"
                    {...register('confirmPassword')}
                    onClick={selectAllText}
                    className={`${authInputClass} pr-10 ${errors.confirmPassword ? 'border-red-500 focus-visible:ring-red-200' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Terms & Conditions */}
              <div className="flex items-start gap-2">
                <Controller
                  control={control}
                  name="acceptTerms"
                  render={({ field }) => (
                    <Checkbox
                      id="acceptTerms"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      onBlur={field.onBlur}
                      className="mt-0.5"
                    />
                  )}
                />
                <div className="grid min-w-0 gap-1.5 leading-snug">
                  <Label
                    htmlFor="acceptTerms"
                    className="cursor-pointer text-xs font-normal sm:text-sm"
                  >
                    <span className="sm:hidden">
                      I agree to the{' '}
                      <a href="#" className="text-primary hover:underline">
                        Terms
                      </a>{' '}
                      &{' '}
                      <a href="#" className="text-primary hover:underline">
                        Privacy Policy
                      </a>
                    </span>
                    <span className="hidden sm:inline">
                      I agree to the{' '}
                      <a href="#" className="text-primary hover:underline">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-primary hover:underline">
                        Privacy Policy
                      </a>
                    </span>
                  </Label>
                  {errors.acceptTerms && (
                    <p className="text-xs text-red-500">{errors.acceptTerms.message}</p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                variant="default"
                className="w-full mt-2 font-semibold"
                disabled={isSubmitting || registerMutation.isPending}
              >
                {(isSubmitting || registerMutation.isPending) ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-5 sm:my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-transparent" />
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="bg-card px-2 text-slate-600">Already have an account?</span>
              </div>
            </div>

            {/* Login Link */}
            <Link to="/auth/login">
              <Button variant="outline" className="w-full">
                Sign In Instead
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="mx-auto mt-4 max-w-[min(100%,280px)] text-center text-xs text-muted-foreground sm:mt-6 sm:max-w-none sm:text-sm">
          Need help?{' '}
          <a href="#" className="text-primary hover:underline">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  )
}

export default Register